/**
 * The one chart and map palette.
 *
 * Species colour is keyed by Metsäkeskus species CODE only. Names are refused:
 * a localized label is display copy, and a lookup by name silently mapped
 * "mixed" to other-conifer and unknown strings to a default slot. Families keep
 * one hue (the botanical grouping of the original `SPECIES_CODE_COLORS`) and
 * species that commonly share a chart get distinct shades of that hue.
 *
 * Non-species series (observed/reference, growth, branches) draw from a
 * separate neutral sequence that never reuses a species slot, so a reference
 * line can never read as pine or spruce.
 */

/** Metsäkeskus codes 1–30 plus the three served aggregate codes. */
const SPECIES_TOKEN_BY_CODE: Readonly<Record<number, string>> = Object.freeze({
  1: 'var(--fm-species-pine)', // Mänty — Scots pine
  2: 'var(--fm-species-spruce)', // Kuusi — Norway spruce
  3: 'var(--fm-species-birch)', // Rauduskoivu — silver birch
  4: 'var(--fm-species-birch-2)', // Hieskoivu — downy birch
  5: 'var(--fm-species-aspen)', // Haapa — aspen
  6: 'var(--fm-species-alder)', // Harmaaleppä — grey alder
  7: 'var(--fm-species-alder-2)', // Tervaleppä — black alder
  8: 'var(--fm-species-other)', // Muu havupuu — other conifer
  9: 'var(--fm-species-other-2)', // Muu lehtipuu — other broadleaf
  10: 'var(--fm-species-pine-2)', // Douglaskuusi — Douglas fir
  11: 'var(--fm-species-other)', // Kataja — juniper
  12: 'var(--fm-species-pine-2)', // Kontortamänty — lodgepole pine
  13: 'var(--fm-species-other-2)', // Kynäjalava — European white elm
  14: 'var(--fm-species-spruce-2)', // Lehtikuusi — larch
  15: 'var(--fm-species-other-2)', // Metsälehmus — small-leaved lime
  16: 'var(--fm-species-spruce-2)', // Mustakuusi — black spruce
  17: 'var(--fm-species-other-2)', // Paju — willow
  18: 'var(--fm-species-other-2)', // Pihlaja — rowan
  19: 'var(--fm-species-spruce-2)', // Pihta — fir
  20: 'var(--fm-species-other-2)', // Raita — goat willow
  21: 'var(--fm-species-other-2)', // Saarni — ash
  22: 'var(--fm-species-pine-2)', // Sembramänty — Swiss stone pine
  23: 'var(--fm-species-spruce-2)', // Serbiankuusi — Serbian spruce
  24: 'var(--fm-species-oak)', // Tammi — oak
  25: 'var(--fm-species-other-2)', // Tuomi — bird cherry
  26: 'var(--fm-species-other-2)', // Vaahtera — maple
  27: 'var(--fm-species-birch-3)', // Visakoivu — curly birch
  28: 'var(--fm-species-other-2)', // Vuorijalava — wych elm
  29: 'var(--fm-species-other-2)', // Lehtipuu — broadleaf (aggregate)
  30: 'var(--fm-species-other)', // Havupuu — conifer (aggregate)
  103: 'var(--fm-species-birch)', // Koivu — birch (Metsäkeskus aggregate)
  106: 'var(--fm-species-alder)', // Leppä — alder (Metsäkeskus aggregate)
  201: 'var(--fm-species-other-2)', // Sekapuu — mixed wood (Metsäkeskus aggregate)
});

/** Every species code the palette colours, in ascending order. */
export const SPECIES_CODES: readonly number[] = Object.freeze(
  Object.keys(SPECIES_TOKEN_BY_CODE)
    .map(Number)
    .sort((a, b) => a - b),
);

/** Raised for a species value the palette refuses; the message names the value. */
export class ChartPaletteError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ChartPaletteError';
  }
}

/**
 * Parse a served species code. API payloads carry codes as numbers or numeric
 * strings ("1"); anything else is a name or garbage and is refused by value.
 */
export function parseSpeciesCode(value: number | string): number {
  const text = String(value).trim();
  if (!/^\d+$/.test(text)) {
    throw new ChartPaletteError(
      `Species colour needs a species code; received the name "${text}". Pass the Metsäkeskus code (1–30).`,
    );
  }
  const code = Number(text);
  if (!(code in SPECIES_TOKEN_BY_CODE)) {
    throw new ChartPaletteError(
      `Unknown species code ${code}: no palette colour is defined for it.`,
    );
  }
  return code;
}

/** Colour of one species code. Unknown codes and names throw, naming the value. */
export function paletteSpecies(code: number): string {
  if (!Number.isInteger(code) || !(code in SPECIES_TOKEN_BY_CODE)) {
    throw new ChartPaletteError(
      `Unknown species code ${String(code)}: no palette colour is defined for it.`,
    );
  }
  return SPECIES_TOKEN_BY_CODE[code];
}

/** Semantic evidence-role emphasis shared by chart marks and their legend swatches. */
export function paletteEvidenceRoleOpacity(role?: 'reference' | 'result'): number {
  return role === 'reference' ? 0.58 : 1;
}

export type FmChartTone = 'accent' | 'teal' | 'amber' | 'sky' | 'rose' | 'neutral';

const TONE_TOKEN: Readonly<Record<FmChartTone, string>> = {
  accent: 'var(--fm-accent)',
  teal: 'var(--fm-teal)',
  amber: 'var(--fm-amber)',
  sky: 'var(--fm-sky)',
  rose: 'var(--fm-rose)',
  neutral: 'var(--fm-d-fg-2)',
};

/** Brand-role colour for a semantic tone. */
export function paletteTone(tone: FmChartTone): string {
  return TONE_TOKEN[tone];
}

/**
 * Neutral sequence for non-species series. Ink first, then brand roles; sky
 * comes last because it sits nearest the spruce hue.
 */
const SERIES_SEQUENCE: readonly string[] = [
  'var(--fm-d-fg)',
  'var(--fm-amber)',
  'var(--fm-teal)',
  'var(--fm-accent)',
  'var(--fm-rose)',
  'var(--fm-sky)',
];

/** Colour of the n-th non-species series; wraps after six. */
export function paletteSeries(index: number): string {
  return SERIES_SEQUENCE[Math.abs(Math.trunc(index)) % SERIES_SEQUENCE.length];
}

/**
 * Line pattern of the n-th line in one chart. Neighbouring lines differ by
 * pattern as well as hue, so series whose colours sit close (three pulpwood
 * prices, a colour-blind reader) still tell apart; the legend swatch draws
 * the same pattern.
 */
export type FmSeriesPattern = 'solid' | 'dashed' | 'dotted';

const SERIES_PATTERNS: readonly FmSeriesPattern[] = ['solid', 'dashed', 'dotted'];

/** SVG dash array of a pattern; `undefined` is a solid stroke. */
export const SERIES_PATTERN_DASH: Readonly<Record<FmSeriesPattern, string | undefined>> = {
  solid: undefined,
  dashed: '7 4',
  dotted: '2 3',
};

export function paletteSeriesPattern(index: number): FmSeriesPattern {
  return SERIES_PATTERNS[Math.abs(Math.trunc(index)) % SERIES_PATTERNS.length];
}

export type FmAssortmentKey = 'sawlog' | 'pulpwood' | 'energy';

const ASSORTMENT_TOKEN: Readonly<Record<FmAssortmentKey, string>> = {
  sawlog: 'var(--fm-assortment-sawlog)',
  pulpwood: 'var(--fm-assortment-pulpwood)',
  energy: 'var(--fm-assortment-energy)',
};

export function isAssortmentKey(value: string): value is FmAssortmentKey {
  return value in ASSORTMENT_TOKEN;
}

export function paletteAssortment(key: FmAssortmentKey): string {
  return ASSORTMENT_TOKEN[key];
}

export type FmEconomicsKey = 'income' | 'cost' | 'net' | 'net-pos' | 'net-neg';

const ECONOMICS_TOKEN: Readonly<Record<FmEconomicsKey, string>> = {
  income: 'var(--fm-chart-income)',
  cost: 'var(--fm-chart-cost)',
  net: 'var(--fm-chart-net)',
  'net-pos': 'var(--fm-chart-net-pos)',
  'net-neg': 'var(--fm-chart-net-neg)',
};

export function paletteEconomics(key: FmEconomicsKey): string {
  return ECONOMICS_TOKEN[key];
}

export type FmUrgencyLevel = 'immediate' | 'short-term' | 'medium-term' | 'long-term';

const URGENCY_TOKEN: Readonly<Record<FmUrgencyLevel, string>> = {
  immediate: 'var(--fm-urgency-immediate)',
  'short-term': 'var(--fm-urgency-short-term)',
  'medium-term': 'var(--fm-urgency-medium-term)',
  'long-term': 'var(--fm-urgency-long-term)',
};

export function paletteUrgency(level: FmUrgencyLevel): string {
  return URGENCY_TOKEN[level];
}

export const FM_OPERATION_COLOR_KEYS = [
  'thinning',
  'seed-tree',
  'clearcut',
  'planting',
  'tending',
  'untouched',
] as const;
export type FmOperationColorKey = (typeof FM_OPERATION_COLOR_KEYS)[number];

export function isOperationColorKey(value: unknown): value is FmOperationColorKey {
  return typeof value === 'string' && (FM_OPERATION_COLOR_KEYS as readonly string[]).includes(value);
}

const OPERATION_TOKEN: Readonly<Record<FmOperationColorKey, string>> = {
  thinning: 'var(--fm-operation-thinning)',
  'seed-tree': 'var(--fm-operation-seed-tree)',
  clearcut: 'var(--fm-operation-clearcut)',
  planting: 'var(--fm-operation-planting)',
  tending: 'var(--fm-operation-tending)',
  untouched: 'var(--fm-operation-untouched)',
};

export function paletteOperation(type: FmOperationColorKey): string {
  return OPERATION_TOKEN[type];
}

/** The removed-in-red portion of a diameter distribution (tender-PDF cut red). */
export const CHART_CUT_COLOR = 'var(--fm-chart-cut)';
export const CHART_CUT_STROKE = 'var(--fm-chart-cut-stroke)';
/** The staying stems as one series beside the cut (tender-PDF green). */
export const CHART_KEPT_COLOR = 'var(--fm-chart-kept)';
